import React, { useState, useEffect, useMemo } from 'react';
import { vendorAPI, purchaseOrderAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Wallet, 
  CreditCard, 
  Users, 
  ArrowUpRight,
  TrendingUp,
  Loader2,
  Search,
  FileText,
  IndianRupee,
  AlertCircle,
  CheckCircle2,
  Clock,
  RefreshCw,
  Building2,
  ChevronRight,
  Download,
  Link2,
  Filter,
  Package
} from 'lucide-react';
import { formatCurrency, formatDate, cn } from '../lib/utils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';

const CHART_COLORS = {
  pending: 'hsl(0 84.2% 60.2%)',
  paid: 'hsl(142 76% 36%)',
  partial: 'hsl(38 92% 50%)'
};

const PIE_COLORS = ['hsl(142 76% 36%)', 'hsl(38 92% 50%)', 'hsl(0 84.2% 60.2%)'];

export const PaymentsPage = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [selectedVendorId, setSelectedVendorId] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [modeFilter, setModeFilter] = useState('all');
  const [createdByFilter, setCreatedByFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPoIds, setSelectedPoIds] = useState([]);
  const [exportingSelected, setExportingSelected] = useState(false);
  const [itemDialog, setItemDialog] = useState({ open: false, po: null, items: [], loading: false });

  const openItemsDialog = async (po) => {
    setItemDialog({ open: true, po, items: [], loading: true });
    try {
      const res = await purchaseOrderAPI.getById(po.id);
      setItemDialog({ open: true, po: res.data, items: res.data?.items || [], loading: false });
    } catch (e) {
      setItemDialog({ open: true, po, items: [], loading: false });
      toast.error('Failed to load PO items');
    }
  };

  const handleExportSelected = async () => {
    if (selectedPoIds.length === 0) return;
    try {
      setExportingSelected(true);
      const res = await purchaseOrderAPI.downloadBulkPaymentReportPDF(selectedPoIds);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'payment_statement.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${selectedPoIds.length} PO(s) to PDF`);
    } catch (e) {
      toast.error('Failed to export PDF');
    } finally {
      setExportingSelected(false);
    }
  };

  const fetchAnalytics = async (showRefresh = false) => {
    try {
      if (showRefresh) setRefreshing(true);
      else setLoading(true);
      
      const response = await vendorAPI.getPaymentAnalytics(
        selectedVendorId !== 'all' ? selectedVendorId : null
      );
      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Failed to fetch payment analytics:', error);
      toast.error('Failed to load payment data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  useEffect(() => {
    if (analyticsData) {
      fetchAnalytics();
    }
  }, [selectedVendorId]);

  const handleRefresh = () => {
    fetchAnalytics(true);
  };

  const handleExportPDF = async () => {
    try {
      setExporting(true);
      const response = await vendorAPI.downloadPaymentSummaryPDF(
        selectedVendorId !== 'all' ? selectedVendorId : null
      );
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `payments_summary_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Payment summary exported successfully');
    } catch (error) {
      console.error('Failed to export PDF:', error);
      toast.error('Failed to export payment summary');
    } finally {
      setExporting(false);
    }
  };

  // Filter vendors based on search
  const filteredVendors = useMemo(() => {
    if (!analyticsData?.vendors) return [];
    return analyticsData.vendors.filter(v =>
      v.vendor_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.vendor_code?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [analyticsData?.vendors, searchTerm]);

  // Get selected vendor details
  const selectedVendorData = useMemo(() => {
    if (selectedVendorId === 'all' || !analyticsData?.selected_vendor) {
      return null;
    }
    return analyticsData.selected_vendor;
  }, [selectedVendorId, analyticsData]);

  // Filter option lists from the selected vendor's POs
  const poFilterOptions = useMemo(() => {
    const pos = selectedVendorData?.pos || [];
    const clients = Array.from(new Set(pos.map(p => p.client_name).filter(Boolean))).sort();
    const modes = Array.from(new Set(pos.map(p => p.payment_mode).filter(Boolean))).sort();
    const creators = Array.from(new Set(pos.map(p => p.created_by_name).filter(Boolean))).sort();
    return { clients, modes, creators };
  }, [selectedVendorData?.pos]);

  // Filter POs for the selected vendor
  const filteredPOs = useMemo(() => {
    let pos = selectedVendorData?.pos || [];
    if (statusFilter !== 'all') pos = pos.filter(po => po.payment_status === statusFilter);
    if (clientFilter !== 'all') pos = pos.filter(po => po.client_name === clientFilter);
    if (modeFilter !== 'all') pos = pos.filter(po => (po.payment_mode || '') === modeFilter);
    if (createdByFilter !== 'all') pos = pos.filter(po => po.created_by_name === createdByFilter);
    if (dateFrom) pos = pos.filter(po => po.date && po.date >= dateFrom);
    if (dateTo) pos = pos.filter(po => po.date && po.date <= dateTo);
    return pos;
  }, [selectedVendorData?.pos, statusFilter, clientFilter, modeFilter, createdByFilter, dateFrom, dateTo]);

  const allFilteredSelected = filteredPOs.length > 0 && filteredPOs.every(po => selectedPoIds.includes(po.id));
  const toggleSelectAll = (checked) => {
    if (checked) setSelectedPoIds(Array.from(new Set([...selectedPoIds, ...filteredPOs.map(p => p.id)])));
    else setSelectedPoIds(selectedPoIds.filter(id => !filteredPOs.some(p => p.id === id)));
  };
  const togglePo = (id) => {
    setSelectedPoIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // Prepare chart data for vendor breakdown
  const vendorChartData = useMemo(() => {
    if (!analyticsData?.vendors) return [];
    return analyticsData.vendors
      .filter(v => v.total_pending > 0 || v.total_paid > 0)
      .slice(0, 10)
      .map(v => ({
        name: v.vendor_name.length > 15 ? v.vendor_name.substring(0, 15) + '...' : v.vendor_name,
        fullName: v.vendor_name,
        pending: v.total_pending,
        paid: v.total_paid
      }));
  }, [analyticsData?.vendors]);

  // Payment status distribution for pie chart
  const statusDistribution = useMemo(() => {
    if (!analyticsData?.vendors) return [];
    const totals = analyticsData.vendors.reduce((acc, v) => {
      acc.paid += v.paid_count;
      acc.partial += v.partial_count;
      acc.unpaid += v.unpaid_count;
      return acc;
    }, { paid: 0, partial: 0, unpaid: 0 });
    
    return [
      { name: 'Paid', value: totals.paid, color: PIE_COLORS[0] },
      { name: 'Partial', value: totals.partial, color: PIE_COLORS[1] },
      { name: 'Unpaid', value: totals.unpaid, color: PIE_COLORS[2] }
    ].filter(item => item.value > 0);
  }, [analyticsData?.vendors]);

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      partial: 'bg-amber-100 text-amber-800 border-amber-200',
      unpaid: 'bg-rose-100 text-rose-800 border-rose-200'
    };
    const labels = {
      paid: 'Paid',
      partial: 'Partial',
      unpaid: 'Unpaid'
    };
    return (
      <Badge variant="outline" className={cn('font-medium', styles[status] || styles.unpaid)}>
        {labels[status] || 'Unpaid'}
      </Badge>
    );
  };

  if (loading && !analyticsData) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="payments-loading">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading payment data...</p>
        </div>
      </div>
    );
  }

  const summary = analyticsData?.summary || {};

  return (
    <div className="space-y-6" data-testid="payments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Payments Dashboard</h1>
          <p className="text-muted-foreground mt-1">Track vendor payments and outstanding balances</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportPDF}
            disabled={exporting}
            data-testid="export-payments-btn"
          >
            <Download className={cn("h-4 w-4 mr-2", exporting && "animate-pulse")} />
            {exporting ? 'Exporting...' : 'Export PDF'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-payments-btn"
          >
            <RefreshCw className={cn("h-4 w-4 mr-2", refreshing && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="shadow-sm hover:shadow-md transition-shadow duration-200" data-testid="total-pending-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Pending</p>
                <p className="text-2xl font-bold text-rose-600 mt-1">
                  {formatCurrency(summary.grand_total_pending || 0)}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-rose-100 flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-rose-600" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              {summary.vendors_with_pending || 0} vendors with pending dues
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-sm hover:shadow-md transition-shadow duration-200" data-testid="total-paid-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Paid</p>
                <p className="text-2xl font-bold text-emerald-600 mt-1">
                  {formatCurrency(summary.grand_total_paid || 0)}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Across {summary.total_pos || 0} purchase orders
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-sm hover:shadow-md transition-shadow duration-200" data-testid="total-amount-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Amount</p>
                <p className="text-2xl font-bold text-foreground mt-1">
                  {formatCurrency(summary.grand_total_amount || 0)}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <IndianRupee className="h-6 w-6 text-primary" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Total procurement value
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-sm hover:shadow-md transition-shadow duration-200" data-testid="active-vendors-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Vendors</p>
                <p className="text-2xl font-bold text-foreground mt-1">
                  {summary.total_vendors || 0}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Building2 className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              With purchase orders
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Vendor List */}
        <Card className="lg:col-span-4 shadow-sm" data-testid="vendor-list-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Vendors</CardTitle>
            <CardDescription>Select a vendor to view details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search vendors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="vendor-search-input"
              />
            </div>
            
            <div 
              className="space-y-2 max-h-[400px] overflow-y-auto pr-1"
              data-testid="vendor-list"
            >
              <Button
                variant={selectedVendorId === 'all' ? 'default' : 'ghost'}
                className="w-full justify-between h-auto py-3"
                onClick={() => setSelectedVendorId('all')}
                data-testid="vendor-all-btn"
              >
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  <span>All Vendors</span>
                </div>
                <Badge variant="secondary">{analyticsData?.vendors?.length || 0}</Badge>
              </Button>
              
              {filteredVendors.map((vendor) => (
                <Button
                  key={vendor.vendor_id}
                  variant={selectedVendorId === vendor.vendor_id ? 'default' : 'ghost'}
                  className="w-full justify-between h-auto py-3 px-3"
                  onClick={() => setSelectedVendorId(vendor.vendor_id)}
                  data-testid={`vendor-btn-${vendor.vendor_id}`}
                >
                  <div className="text-left">
                    <p className="font-medium truncate max-w-[150px]">{vendor.vendor_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {vendor.total_pos} POs
                    </p>
                  </div>
                  <div className="text-right">
                    {vendor.total_pending > 0 ? (
                      <p className="text-sm font-medium text-rose-600">
                        {formatCurrency(vendor.total_pending)}
                      </p>
                    ) : (
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                        Cleared
                      </Badge>
                    )}
                  </div>
                </Button>
              ))}
              
              {filteredVendors.length === 0 && searchTerm && (
                <div className="text-center py-8 text-muted-foreground">
                  <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No vendors found</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Charts Section */}
        <div className="lg:col-span-8 space-y-6">
          {/* Bar Chart */}
          <Card className="shadow-sm" data-testid="payment-chart-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Vendor Payment Breakdown</CardTitle>
              <CardDescription>Top 10 vendors by payment volume</CardDescription>
            </CardHeader>
            <CardContent>
              {vendorChartData.length > 0 ? (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={vendorChartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="name" 
                        tick={{ fontSize: 11 }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                        interval={0}
                      />
                      <YAxis 
                        tick={{ fontSize: 11 }}
                        tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`}
                      />
                      <Tooltip 
                        formatter={(value, name) => [formatCurrency(value), name === 'pending' ? 'Pending' : 'Paid']}
                        labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                      <Bar dataKey="paid" name="Paid" fill={CHART_COLORS.paid} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="pending" name="Pending" fill={CHART_COLORS.pending} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  <p>No payment data available</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Status Distribution Pie Chart */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="shadow-sm" data-testid="status-distribution-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Payment Status</CardTitle>
                <CardDescription>Distribution across all POs</CardDescription>
              </CardHeader>
              <CardContent>
                {statusDistribution.length > 0 ? (
                  <div className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={statusDistribution}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          labelLine={false}
                        >
                          {statusDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value) => [value, 'POs']}
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                    <p>No data available</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card className="shadow-sm" data-testid="quick-stats-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Quick Stats</CardTitle>
                <CardDescription>Payment overview</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    <span className="text-sm font-medium">Fully Paid POs</span>
                  </div>
                  <span className="text-lg font-bold text-emerald-600">
                    {analyticsData?.vendors?.reduce((acc, v) => acc + v.paid_count, 0) || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-amber-600" />
                    <span className="text-sm font-medium">Partially Paid</span>
                  </div>
                  <span className="text-lg font-bold text-amber-600">
                    {analyticsData?.vendors?.reduce((acc, v) => acc + v.partial_count, 0) || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-rose-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-rose-600" />
                    <span className="text-sm font-medium">Unpaid POs</span>
                  </div>
                  <span className="text-lg font-bold text-rose-600">
                    {analyticsData?.vendors?.reduce((acc, v) => acc + v.unpaid_count, 0) || 0}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Vendor PO Details Table */}
      {selectedVendorData && (
        <Card className="shadow-sm" data-testid="vendor-po-details-card">
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  {selectedVendorData.vendor_name}
                </CardTitle>
                <CardDescription>
                  {selectedVendorData.total_pos} Purchase Orders • 
                  Pending: <span className="text-rose-600 font-medium">{formatCurrency(selectedVendorData.total_pending)}</span>
                </CardDescription>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {selectedPoIds.length > 0 && (
                  <Button
                    size="sm"
                    onClick={handleExportSelected}
                    disabled={exportingSelected}
                    data-testid="export-selected-pdf-btn"
                  >
                    {exportingSelected ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}
                    Export {selectedPoIds.length} PO(s)
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(v => !v)}
                  data-testid="toggle-filters-btn"
                >
                  <Filter className="h-4 w-4 mr-1.5" />
                  Filters
                </Button>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[140px]" data-testid="po-status-filter">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                    <SelectItem value="unpaid">Unpaid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {showFilters && (
              <div className="flex flex-wrap items-end gap-3 mt-4 p-3 bg-muted/30 rounded-lg" data-testid="po-filters-panel">
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-muted-foreground">Client</label>
                  <Select value={clientFilter} onValueChange={setClientFilter}>
                    <SelectTrigger className="w-[160px]" data-testid="filter-client"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Clients</SelectItem>
                      {poFilterOptions.clients.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-muted-foreground">Payment Mode</label>
                  <Select value={modeFilter} onValueChange={setModeFilter}>
                    <SelectTrigger className="w-[150px]" data-testid="filter-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Modes</SelectItem>
                      {poFilterOptions.modes.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-muted-foreground">Created By</label>
                  <Select value={createdByFilter} onValueChange={setCreatedByFilter}>
                    <SelectTrigger className="w-[160px]" data-testid="filter-created-by"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Users</SelectItem>
                      {poFilterOptions.creators.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-muted-foreground">From Date</label>
                  <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-[150px]" data-testid="filter-date-from" />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-muted-foreground">To Date</label>
                  <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-[150px]" data-testid="filter-date-to" />
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setClientFilter('all'); setModeFilter('all'); setCreatedByFilter('all'); setDateFrom(''); setDateTo(''); setStatusFilter('all'); }} data-testid="clear-filters-btn">
                  Clear
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-[40px]">
                      <Checkbox checked={allFilteredSelected} onCheckedChange={toggleSelectAll} data-testid="select-all-pos" />
                    </TableHead>
                    <TableHead>PO Number</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Created By</TableHead>
                    <TableHead>Linked GRN</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Paid</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead>Payment Mode</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPOs.length > 0 ? (
                    filteredPOs.map((po) => (
                      <TableRow 
                        key={po.id} 
                        className="hover:bg-muted/30 transition-colors"
                        data-testid={`po-row-${po.id}`}
                      >
                        <TableCell>
                          <Checkbox checked={selectedPoIds.includes(po.id)} onCheckedChange={() => togglePo(po.id)} data-testid={`select-po-${po.id}`} />
                        </TableCell>
                        <TableCell>
                          <button
                            className="font-medium text-primary hover:underline inline-flex items-center gap-1"
                            onClick={() => openItemsDialog(po)}
                            data-testid={`po-number-link-${po.id}`}
                          >
                            <Package className="h-3.5 w-3.5" />
                            {po.po_number}
                          </button>
                        </TableCell>
                        <TableCell>{formatDate(po.date)}</TableCell>
                        <TableCell>{po.client_name || '-'}</TableCell>
                        <TableCell className="text-sm">{po.created_by_name || '-'}</TableCell>
                        <TableCell>
                          {po.grn_numbers && po.grn_numbers.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {po.grn_numbers.map((g, i) => (
                                <Badge key={i} variant="outline" className="gap-1 font-mono text-xs">
                                  <Link2 className="h-3 w-3" />{g}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-xs">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(po.total_amount)}
                        </TableCell>
                        <TableCell className="text-right text-emerald-600">
                          {formatCurrency(po.payment_made)}
                        </TableCell>
                        <TableCell className="text-right text-rose-600 font-medium">
                          {formatCurrency(po.balance_due)}
                        </TableCell>
                        <TableCell>{po.payment_mode || '-'}</TableCell>
                        <TableCell>{getStatusBadge(po.payment_status)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                        No purchase orders found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            
            {/* Vendor Summary Footer */}
            <div className="mt-4 p-4 bg-muted/30 rounded-lg flex flex-wrap gap-6 justify-end">
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Total Amount</p>
                <p className="text-lg font-bold">{formatCurrency(selectedVendorData.total_amount)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Total Paid</p>
                <p className="text-lg font-bold text-emerald-600">{formatCurrency(selectedVendorData.total_paid)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Total Pending</p>
                <p className="text-lg font-bold text-rose-600">{formatCurrency(selectedVendorData.total_pending)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State when no vendor selected */}
      {selectedVendorId === 'all' && (
        <Card className="shadow-sm border-dashed" data-testid="select-vendor-prompt">
          <CardContent className="py-12">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Building2 className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Select a Vendor</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Choose a vendor from the list to view detailed payment information and purchase order history.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* PO Items Dialog */}
      <Dialog open={itemDialog.open} onOpenChange={(o) => setItemDialog(prev => ({ ...prev, open: o }))}>
        <DialogContent className="max-w-2xl" data-testid="po-items-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              {itemDialog.po?.po_number} — Items
            </DialogTitle>
            <DialogDescription>
              {itemDialog.po?.client_name ? `Client: ${itemDialog.po.client_name} · ` : ''}
              {(itemDialog.po?.po_type === 'service') ? 'Service PO' : 'Goods PO'}
            </DialogDescription>
          </DialogHeader>
          {itemDialog.loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : itemDialog.items.length > 0 ? (
            <div className="border rounded-lg overflow-hidden max-h-[60vh] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-[40px]">#</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Rate</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {itemDialog.items.map((it, i) => (
                    <TableRow key={i} data-testid={`po-item-row-${i}`}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{it.item_name || it.description || '-'} {it.unit_name || it.unit ? <span className="text-xs text-muted-foreground">({it.unit_name || it.unit})</span> : null}</TableCell>
                      <TableCell className="text-right">{it.quantity}</TableCell>
                      <TableCell className="text-right">{formatCurrency(it.rate || 0)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(it.amount || 0)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No items found for this PO.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentsPage;
